import type { BannerSchedule } from '@/services';
import { safeJsonStringify } from '@/utils/sanitize';

/**
 * 배너 노출 판정 — 첫 페인트 전 인라인 스크립트와 하이드레이션 후 React 가 같은 규칙을 쓴다.
 *
 * 정적 페이지는 다음 배포까지 쓰이므로 "지금"을 아는 건 브라우저뿐이다. 그래서 오픈/종료와
 * "24시간 닫기"를 서버가 아니라 여기서 판정한다. 스크립트는 문자열이라 이 함수를 그대로
 * 쓸 수 없어 같은 규칙을 한 번 더 적는다 — 두 구현이 같은 답을 내는지는 테스트가 대조한다.
 */

export type BannerRule = BannerSchedule & {
  /** "24시간 닫기" 허용 여부 — 노출 대상 중 하나라도 허용하면 닫기 버튼을 둔다 */
  closable: boolean;
};

/**
 * 숨김 상태는 배너 하나가 아니라 **여러 배너를 동시에** 기억해야 한다.
 * 같은 프로모션이라도 국문·영문이 서로 다른 WordPress 포스트(= 다른 id)라서,
 * 한 칸짜리 저장소를 쓰면 언어를 바꾸는 순간 반대 언어의 숨김이 지워진다.
 */
export const BANNER_HIDDEN_STORAGE_KEY = 'bannerHiddenUntilById';

export type HiddenUntilById = Record<string, number>;

/** 손상된 값이 섞여도 숫자 항목만 살린다 */
export function parseHiddenUntilById(raw: string | null): HiddenUntilById {
  if (!raw) return {};

  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return Object.fromEntries(
      Object.entries(parsed).filter(
        (entry): entry is [string, number] => typeof entry[1] === 'number',
      ),
    );
  } catch {
    return {};
  }
}

/** 저장소 접근 예외(차단·사파리 프라이빗 등)는 호출부가 처리한다 */
export const readHiddenUntilById = () =>
  parseHiddenUntilById(localStorage.getItem(BANNER_HIDDEN_STORAGE_KEY));

/**
 * 닫기 기록을 읽으면서 만료된 항목은 지운다 — 프로모션이 쌓여도 저장소가 계속 커지지 않게.
 * 저장소 접근 예외는 호출부가 처리한다.
 */
export function readAndPruneHiddenUntilById(now: number): HiddenUntilById {
  const stored = readHiddenUntilById();
  const alive = Object.fromEntries(
    Object.entries(stored).filter(([, until]) => now <= until),
  );
  if (Object.keys(alive).length !== Object.keys(stored).length) {
    localStorage.setItem(BANNER_HIDDEN_STORAGE_KEY, JSON.stringify(alive));
  }
  return alive;
}

/** 노출할 배너 id — 서버가 준 순서(최신순) 유지. 경계는 openedAt ≤ now < closedAt */
export function getVisibleBannerIds(
  rules: BannerRule[],
  hiddenUntilById: HiddenUntilById,
  now: number,
): string[] {
  return rules
    .filter(
      rule =>
        rule.openedAt <= now &&
        (rule.closedAt === null || now < rule.closedAt) &&
        !(hiddenUntilById[rule.id] > now),
    )
    .map(rule => rule.id);
}

// ⚠️ 아래 두 상수의 속성 이름은 globals.css 의 "헤더 배너 인계 규칙"과 짝이다 — 바꾸면
// CSS 선택자도 함께 바꿔야 한다(테스트가 globals.css 에 같은 이름이 있는지 대조한다).

// 스크립트가 <html> 에 남기는 표시. globals.css 의 인계 규칙이 이 표시로 하이드레이션 전
// 화면을 맞추고, React 가 판정을 마치면 READY 를 켜서 그 규칙을 끈다.
export const BANNER_ATTR = {
  VISIBLE: 'data-banner-visible',
  MULTI: 'data-banner-multi',
  CLOSABLE: 'data-banner-closable',
  READY: 'data-banner-ready',
} as const;

// 인계 규칙이 겨냥하는 배너 마크업 요소의 표시
export const BANNER_HOOK = {
  ROOT: 'data-banner-root',
  SLIDE: 'data-banner-slide',
  BODY: 'data-banner-body',
  ROTATION: 'data-banner-rotation',
  CLOSE: 'data-banner-close',
} as const;

/** 판정을 마친 React 가 배너를 소유한다 — 인계 CSS 규칙을 끈다 */
export const markBannerReady = () =>
  document.documentElement.setAttribute(BANNER_ATTR.READY, '');

/**
 * 배너 마크업 바로 앞에 두는 동기 스크립트. 파서는 이 스크립트를 실행한 뒤에야 아래 배너를
 * 그리므로 결과가 첫 페인트에 반영된다.
 * - 노출 대상 슬라이드만 보이게 하는 id 별 규칙을 <style> 로 넣는다 — 대상만 넣어 HTML·RSC
 *   페이로드에 배너 수만큼 CSS 를 싣지 않는다. id 는 서버가 형식을 검증해 선택자에 바로 쓴다.
 * - localStorage 예외는 "닫지 않음"으로 보고 일정 판정은 계속한다.
 * - 스크립트가 실패하면 표시가 없어 배너는 숨겨진다(종료된 배너 노출 방지).
 */
export function buildBannerEligibilityScript(rules: BannerRule[]): string {
  const data = safeJsonStringify(
    rules.map(({ id, openedAt, closedAt, closable }) => ({
      i: id,
      o: openedAt,
      c: closedAt,
      x: closable,
    })),
  );
  const key = safeJsonStringify(BANNER_HIDDEN_STORAGE_KEY);
  const slide = `html:not([${BANNER_ATTR.READY}]) [${BANNER_HOOK.ROOT}] [${BANNER_HOOK.SLIDE}="`;

  return `(function(){try{var r=${data},h={},d=document.documentElement;try{var p=JSON.parse(localStorage.getItem(${key})||'{}');if(p&&typeof p==='object'&&!Array.isArray(p))h=p}catch(e){}var n=Date.now(),v=[],x=false;for(var k=0;k<r.length;k++){var b=r[k],u=h[b.i];if(b.o<=n&&(b.c===null||n<b.c)&&!(typeof u==='number'&&u>n)){v.push(b.i);if(b.x)x=true}}if(!v.length)return;var s='';for(k=0;k<v.length;k++)s+='${slide}'+v[k]+'"]{display:block}';s+='${slide}'+v[0]+'"]{visibility:visible}';var t=document.createElement('style');t.textContent=s;document.head.appendChild(t);d.setAttribute('${BANNER_ATTR.VISIBLE}',v.join(' '));if(v.length>1)d.setAttribute('${BANNER_ATTR.MULTI}','');if(x)d.setAttribute('${BANNER_ATTR.CLOSABLE}','')}catch(e){}})()`;
}

/**
 * 배너 마크업 바로 뒤에 두는 동기 스크립트 — 배너의 실제 높이를 `--banner-height` 로 즉시 쓴다.
 * 본문이 파싱되기 전이라 이 값을 쓰는 오프셋(앵커 scroll-margin, sticky 레일 등)이 첫 페인트와
 * 첫 진입 해시 이동부터 맞는다. 이후 폰트 교체 등으로 높이가 바뀌면 useBannerHeightSync 가 갱신한다.
 */
export const BANNER_HEIGHT_SCRIPT = `(function(){try{var r=document.querySelectorAll('[${BANNER_HOOK.ROOT}]'),e=r[r.length-1];if(e)document.documentElement.style.setProperty('--banner-height',e.offsetHeight+'px')}catch(x){}})()`;
